import React, { useState, useEffect, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { StyledDialog, StyledButton } from './common/StyledInputs';
import { useError } from '../contexts/ErrorContext';
import { formatCurrency } from '../utils/currency';
import { getReimbursementRequestMatches, handleReimbursementRequest } from '../services/api';

const REIMBURSEMENT_MATCH_WINDOW_DAYS = 90;

const getTransactionAmountCents = (transaction) => (
  Number.isFinite(transaction?.amountCents)
    ? Math.abs(transaction.amountCents)
    : Math.abs(Math.round((transaction?.amount || 0) * 100))
);

const ReimbursementMatchModal = ({ open, onClose, alert, onSuccess }) => {
  const { showError, showSuccess } = useError();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [matching, setMatching] = useState(false);

  const fetchTransactions = useCallback(async () => {
    const reimbursementTransactionId = alert?.metadata?.reimbursementTransactionId;
    if (!reimbursementTransactionId) {
      setTransactions([]);
      return;
    }

    try {
      setLoading(true);
      setSelectedTransactionId(null);
      const response = await getReimbursementRequestMatches(
        reimbursementTransactionId,
        REIMBURSEMENT_MATCH_WINDOW_DAYS
      );
      setTransactions(response.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      const message = error.response?.data?.error;
      showError(typeof message === 'string' ? message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [alert, showError]);

  useEffect(() => {
    if (open && alert?.metadata?.reimbursementTransactionId) {
      fetchTransactions();
    }
  }, [open, alert, fetchTransactions]);

  const handleMatch = async () => {
    if (!selectedTransactionId || !alert?.metadata?.reimbursementTransactionId) {
      return;
    }

    try {
      setMatching(true);
      await handleReimbursementRequest(
        alert.metadata.reimbursementTransactionId,
        'paid',
        selectedTransactionId,
        REIMBURSEMENT_MATCH_WINDOW_DAYS
      );
      
      showSuccess('Reimbursement matched successfully');
      if (onSuccess) {
        onSuccess('Reimbursement matched successfully');
      }
      onClose();
    } catch (error) {
      console.error('Error matching reimbursement:', error);
      const message = error.response?.data?.error;
      showError(typeof message === 'string' ? message : 'Failed to match reimbursement');
    } finally {
      setMatching(false);
    }
  };

  const reimbursementAmount = alert?.metadata?.amountCents 
    ? formatCurrency(alert.metadata.amountCents / 100)
    : 'N/A';

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Match Reimbursement</Typography>
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a transaction to match with this reimbursement request ({reimbursementAmount}).
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No matching transactions found.
            </Typography>
          </Box>
        ) : (
          <List>
            {transactions.map((transaction, index) => (
              <React.Fragment key={transaction._id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedTransactionId === transaction._id}
                    onClick={() => setSelectedTransactionId(transaction._id)}
                  >
                    <ListItemText
                      primary={transaction.description || transaction.merchantName}
                      secondary={`${formatCurrency(getTransactionAmountCents(transaction) / 100)} • ${new Date(transaction.authorizedDate || transaction.date).toLocaleDateString()}`}
                    />
                  </ListItemButton>
                </ListItem>
                {index < transactions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <StyledButton
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleMatch}
          disabled={!selectedTransactionId || matching || transactions.length === 0}
        >
          {matching ? 'Matching...' : 'Match Transaction'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ReimbursementMatchModal;
